import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Button, Dialog, Icon, Textarea, Typography, Popover, Progress } from '@equinor/eds-core-react'
import { close, help_outline } from '@equinor/eds-icons'
import { baseFont, VContainer } from '@/styles/layout'
import { useConfirmDialog, useInfoDialog } from '@/hooks'
import { insertImage } from '@/api/apiService'
import { addSnackBarMessage } from '@/core/store/globalStore'
import { useQueryClient } from '@tanstack/react-query'
import { QueryKeys } from '@/core/models/enum'

interface ImageProps {
    plant: string
    tag: string
    showImage: boolean
    onClose: () => void
}

const tooltipImage = 'Describe the purpose of this image. Max 240 characters.'

const SelectImageFromLibrary: React.FC<ImageProps> = ({ plant, tag, showImage, onClose }) => {
    const [file, setFile] = useState<File | null>(null)
    const [previewUrl, setPreviewUrl] = useState<string | null>(null)
    const [description, setDescription] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [isUploading, setIsUploading] = useState(false)
    const [imageEdited, setImageEdited] = useState(false)
    const [isTooltipDescriptionOpen, setTooltipDescriptionIsOpen] = useState(false)
    const descriptionRef = useRef<HTMLDivElement>(null)

    const fileInputRef = useRef<HTMLInputElement>(null)
    const didAutoOpenRef = useRef(false)

    const queryClient = useQueryClient()

    const { showConfirm } = useConfirmDialog({
        title: 'Unsaved changes',
        content: 'You have unsaved changes. Are you sure you want to proceed?',
        rejectButtonTitle: 'Discard changes',
        confirmButtonTitle: 'Stay on page',
        onConfirm: () => {},
        onReject: () => onClose(),
        autoClose: true,
    })

    const { showInfo } = useInfoDialog(
        {
            title: 'Access Required',
            content: (
                <VContainer $gap="16px">
                    <Typography>
                        You do not have permission to upload image. Please request the role “STID Read Internal” for
                        relevant plant in AccessIT to get access.
                    </Typography>
                </VContainer>
            ),
            type: 'Info',
        },
        []
    )

    useEffect(() => {
        if (!showImage) {
            didAutoOpenRef.current = false
            return
        }

        setError(null)
        setFile(null)
        setDescription('')
        setIsUploading(false)
        setImageEdited(false)

        setPreviewUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev)
            return null
        })

        return () => {
            setPreviewUrl((prev) => {
                if (prev) URL.revokeObjectURL(prev)
                return null
            })
        }
    }, [showImage])

    const handleChangeDescription = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setImageEdited(true)
        setDescription(e.target.value)
    }

    const handleClose = () => {
        if (imageEdited) showConfirm()
        else onClose()
    }

    const openFilePicker = () => {
        setError(null)
        fileInputRef.current?.click()
    }

    const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const picked = e.target.files?.[0] ?? null
        if (!picked) return

        if (!picked.type.startsWith('image/')) {
            setError('Only image files are allowed.')
            e.target.value = ''
            return
        }

        setImageEdited(true)
        setError(null)
        setFile(picked)

        setPreviewUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev)
            return URL.createObjectURL(picked)
        })

        // Allow selecting the same file again later
        e.target.value = ''
    }

    const clearSelection = () => {
        setImageEdited(true)
        setError(null)
        setFile(null)
        setPreviewUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev)
            return null
        })

        setTimeout(() => fileInputRef.current?.click(), 0)
    }

    const upload = async () => {
        if (!file) return

        setIsUploading(true)
        setError(null)

        try {
            const response = await insertImage(plant, tag, file, description)
            if (response) {
                setImageEdited(false)
                addSnackBarMessage(`Image added`)
                queryClient.invalidateQueries({ queryKey: [QueryKeys.TagDetails] })
            } else {
                const message = `Failed to insert image for ${tag}.`
                addSnackBarMessage(message)
                showInfo()
                return
            }
            onClose()
        } catch (e: any) {
            setError(e?.message ?? `Failed to insert image for ` + tag)
        } finally {
            setIsUploading(false)
        }
    }

    return (
        <ImageContainer color="black" open={showImage} onClose={handleClose} isDismissable>
            <Header>
                <HeaderLabel>Select image</HeaderLabel>
                <CloseButton as="Link" onClick={handleClose}>
                    <Icon data={close} size={24} style={{ color: '#007079' }} />
                </CloseButton>
            </Header>

            {error && <ErrorText>{error}</ErrorText>}

            {!error && !previewUrl && (
                <EmptyState>
                    <Typography variant="body_short" style={{ marginBottom: 12 }}>
                        Choose an image from your device.
                    </Typography>

                    <Button onClick={openFilePicker} disabled={isUploading}>
                        Select image
                    </Button>
                </EmptyState>
            )}

            {previewUrl && (
                <>
                    <ImageView src={previewUrl} alt="Selected image" />

                    <StyledTextArea
                        label={
                            <RowStyle>
                                <InputStyledLabel>Image description</InputStyledLabel>
                                <div ref={descriptionRef}>
                                    <Button
                                        variant="ghost_icon"
                                        aria-label="description help"
                                        onClick={() => setTooltipDescriptionIsOpen(!isTooltipDescriptionOpen)}
                                    >
                                        <Icon data={help_outline} size={24} />
                                    </Button>
                                </div>
                            </RowStyle>
                        }
                        meta={
                            <RowStyle>
                                <InputStyledLabel>Mandatory</InputStyledLabel>
                            </RowStyle>
                        }
                        id="UserAddedDescription"
                        value={description ?? ''}
                        onChange={handleChangeDescription}
                        required
                        rows={3}
                        maxLength={240}
                        placeholder="Add description"
                        disabled={isUploading}
                    />

                    <RowContainerBottom>
                        <Button as="button" onClick={clearSelection} variant="outlined" disabled={isUploading}>
                            <Label>Change</Label>
                        </Button>
                        <Button as="button" onClick={upload} disabled={isUploading || !file || !description.trim()}>
                            {isUploading ? <Progress.Dots /> : <StyledActionLabel>Upload</StyledActionLabel>}
                        </Button>
                    </RowContainerBottom>
                </>
            )}

            <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onPickFile} />

            <Popover
                open={isTooltipDescriptionOpen}
                anchorEl={descriptionRef.current}
                onClose={() => setTooltipDescriptionIsOpen(false)}
                style={{ width: '300px' }}
            >
                <Popover.Header>
                    <Popover.Title>Image description</Popover.Title>
                    <Button
                        style={{ height: '32px', width: '32px' }}
                        variant="ghost_icon"
                        aria-label="Close popover"
                        onClick={() => setTooltipDescriptionIsOpen(false)}
                    >
                        <Icon data={close} size={24} />
                    </Button>
                </Popover.Header>
                <Popover.Content>
                    <StyledPopoverContent variant="body_short">{tooltipImage}</StyledPopoverContent>
                </Popover.Content>
            </Popover>
        </ImageContainer>
    )
}

const StyledActionLabel = styled(Typography)`
    ${baseFont}
    color: white;
    &:hover {
        color: white;
        background: #007079;
    }
`

const ImageView = styled.img`
    max-height: 40vh;
    max-width: 100%;
`

const EmptyState = styled.div`
    width: 100%;
    padding: 16px 0;
    display: flex;
    flex-direction: column;
    align-items: center;
`

const ErrorText = styled(Typography)`
    color: #b30d2f;
    margin: 8px 0 12px;
`

const HeaderLabel = styled(Typography)`
    font-family: Equinor;
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
    letter-spacing: 0.2px;
    color: #3d3d3d;
`

const ImageContainer = styled(Dialog)`
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 550px;
    max-width: calc(100% - 32px);
    padding: 16px;
    max-height: 80vh;

    @media (max-width: 1024px) {
        overflow: hidden;
    }
`

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
`

const CloseButton = styled(Button)`
    ${baseFont}
    color: #007079;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;
    text-decoration: none;
    background: none;
    border: none;
    cursor: pointer;

    &:hover {
        background: white;
    }

    @media (max-width: 1024px) {
        color: #007079;
        &:hover {
            background: white;
        }
    }
`

const StyledTextArea = styled(Textarea)`
    gap: 16px;
    text-decoration: none;
    background: none;
    border: none;
    font-size: 20px;
    font-style: normal;
    font-weight: 500;
    margin-bottom: 0px;

    @media (max-width: 1024px) {
        & > div {
            background: white;
            background-color: white;
        }
    }
`

const RowStyle = styled.div`
    display: flex;
    align-items: center;
    flex-direction: row;
    margin-bottom: 4px;
    gap: 8px;
    background: #ffffff;
    @media (max-width: 1024px) {
        background: none;
    }
    > * {
        margin-right: 0.3rem;
    }
`

const InputStyledLabel = styled(Typography)`
    ${baseFont}
    color: #6f6f6f;
`

const StyledPopoverContent = styled(Typography)`
    max-width: 280px;
    white-space: normal;
    overflow-wrap: anywhere;
`

const RowContainerBottom = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    width: 100%;
    padding: 8px;
    margin-bottom: 0;
    margin-right: 8px;
    gap: 16px;
`

const Label = styled.span`
    font-family: Equinor;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
`

export default SelectImageFromLibrary
